import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useRef, useState, } from 'react';
import { cx } from '../internal/variants.js';
import { Icon } from './Icon.js';
export function Field({ label, hint, hintTone, children }) {
    return (_jsxs("label", { className: "field", children: [label && _jsx("span", { className: "flab", children: label }), children, hint && _jsx("span", { className: cx('fhint', hintTone), children: hint })] }));
}
export function Input({ mono = false, tone, className, ...rest }) {
    return _jsx("input", { className: cx('input', mono && 'mono', tone, className), ...rest });
}
/** SegmentedControl — disclosure-tier switch (garnet.css `.seg`). */
export function SegmentedControl({ options, value, onChange, ariaLabel, }) {
    return (_jsx("div", { className: "seg", role: "group", "aria-label": ariaLabel, children: options.map((o) => (_jsx("button", { type: "button", "aria-pressed": value === o.v, onClick: () => onChange(o.v), children: o.label }, o.v))) }));
}
/**
 * Select — branded dropdown rendered as an in-page listbox popover (garnet.css
 * `.gselect`), keyboard-navigable, no native OS chrome. Faithful port of the console
 * primitive; presentational only (state is local UI state, no data fetching).
 */
export function Select({ value, onChange, options, placeholder, mono = false, ariaLabel, disabled = false, }) {
    const opts = options.map((o) => typeof o === 'object' ? o : { value: o, label: String(o) });
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const ref = useRef(null);
    const listId = useId();
    const sel = opts.find((o) => o.value === value);
    useEffect(() => {
        if (!open)
            return;
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target))
                setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);
    function openAt() {
        setActive(Math.max(0, opts.findIndex((o) => o.value === value)));
        setOpen(true);
    }
    function onKey(e) {
        if (disabled)
            return;
        if (e.key === 'Escape') {
            setOpen(false);
            return;
        }
        if (!open) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault();
                openAt();
            }
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(opts.length - 1, a + 1));
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(0, a - 1));
        }
        else if (e.key === 'Enter') {
            e.preventDefault();
            const opt = opts[active];
            if (opt) {
                onChange(opt.value);
                setOpen(false);
            }
        }
        else if (e.key === 'Tab') {
            setOpen(false);
        }
    }
    return (_jsxs("div", { className: "gselect", ref: ref, children: [_jsxs("button", { type: "button", className: cx('gselect-trigger', mono && 'mono', open && 'open'), "aria-haspopup": "listbox", "aria-expanded": open, "aria-controls": open ? listId : undefined, "aria-label": ariaLabel, disabled: disabled, onClick: () => (open ? setOpen(false) : openAt()), onKeyDown: onKey, children: [_jsx("span", { className: sel ? 'gselect-val' : 'gselect-ph', children: sel ? sel.label : placeholder ?? 'Select…' }), _jsx(Icon, { name: "chevD", size: 15, stroke: 2 })] }), open && (_jsx("ul", { className: cx('gselect-menu', mono && 'mono'), id: listId, role: "listbox", tabIndex: -1, children: opts.map((o, i) => (_jsxs("li", { role: "option", "aria-selected": o.value === value, className: cx('gselect-opt', o.value === value && 'sel', i === active && 'active'), onMouseEnter: () => setActive(i), onMouseDown: (e) => {
                        e.preventDefault();
                        onChange(o.value);
                        setOpen(false);
                    }, children: [_jsx("span", { children: o.label }), o.value === value && (_jsx(Icon, { name: "check", size: 14, style: { color: 'var(--verified)', flex: 'none' } }))] }, String(o.value)))) }))] }));
}
//# sourceMappingURL=Field.js.map